import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'

import { Box } from '@/components/ui'
import type { IntakeFormListItem } from '@/definitions/intakeForm'

import { IntakeFormActionsMenu } from '../IntakeFormActionsMenu'

const COPIED_RESET_MS = 2000

const COPY_PROMPT = 'Copy patient link'
const COPY_DONE = 'Copied!'
const COPY_FAILED = 'Could not copy — check clipboard permissions'
const EDIT_PROMPT = 'Edit title and text'
const REPLACE_PROMPT = 'Replace the questions'

type CopyState = 'idle' | 'copied' | 'failed'

export interface IntakeFormRowProps {
  form: IntakeFormListItem
  /** Opens the text editor. Offered for the generic form only. */
  onEdit: (form: IntakeFormListItem) => void
  /** Opens the revised-definition box. Offered for a custom form only. */
  onReplace: (form: IntakeFormListItem) => void
  /** This form's own on/off change is in flight. */
  isActionPending: boolean
  /** No on/off change can be started right now, whichever form it would be for. */
  isActionDisabled: boolean
  onSetEnabled: (enabled: boolean) => void
}

/**
 * One intake form and its standing: where it came from, whether patients are being
 * given it, and whether it is the organization's primary form.
 *
 * A form that is switched off keeps its row rather than disappearing, because
 * knowing an organization has a form and is not using it is the point.
 *
 * The patient link is copyable rather than shown: it is the only way to see what a
 * form actually serves. The generic form's text can be edited; its questions cannot.
 * A custom form's questions are replaced by supplying a revised definition, which
 * keeps the link already handed out.
 */
export const IntakeFormRow = ({
  form,
  onEdit,
  onReplace,
  isActionPending,
  isActionDisabled,
  onSetEnabled,
}: IntakeFormRowProps) => {
  const [copyState, setCopyState] = useState<CopyState>('idle')

  // The row shows no URL, so a silent failure leaves the operator pasting whatever
  // was in the clipboard before — and sending a patient the wrong link.
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(form.intake_url)
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
    setTimeout(() => setCopyState('idle'), COPIED_RESET_MS)
  }

  const copyTooltip = { idle: COPY_PROMPT, copied: COPY_DONE, failed: COPY_FAILED }[
    copyState
  ]

  return (
    <Paper variant='infoRow'>
      <Box variant='flexBetween'>
        <Box variant='flex' gap='sm' fullWidth={false} minWidth={0}>
          <Typography variant='subtitle2'>{form.display_name}</Typography>
        </Box>

        {/* Named in both directions: an organization with no primary form is the
            common case, and reading that off the row is the point — a chip that
            only appears when set cannot say it. */}
        <Box variant='flex' gap='sm' fullWidth={false}>
          <Chip
            size='small'
            variant={form.is_primary ? 'status' : 'outlined'}
            color={form.is_primary ? 'info' : 'default'}
            label={form.is_primary ? 'Primary' : 'Not primary'}
          />
          <Chip
            size='small'
            variant='outlined'
            label={form.is_generic_form ? 'Generic' : 'Custom'}
          />
          {/* Active/Deactivated in the status colours, because this is the same
              AuditModel.enabled column the organization, location and staff rows
              render — an operator reads red as deactivated across all four. */}
          <Chip
            size='small'
            variant='status'
            label={form.enabled ? 'Active' : 'Deactivated'}
            color={form.enabled ? 'success' : 'error'}
          />
          <Tooltip title={copyTooltip} placement='top' open={copyState === 'failed' || undefined}>
            <IconButton
              size='small'
              onClick={handleCopyLink}
              color={copyState === 'failed' ? 'error' : 'default'}
              aria-label={`Copy patient link for ${form.display_name}`}
            >
              <ContentCopyOutlinedIcon style={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          {form.is_generic_form ? (
            <Tooltip title={EDIT_PROMPT} placement='top'>
              <IconButton
                size='small'
                onClick={() => onEdit(form)}
                aria-label={`Edit title and text for ${form.display_name}`}
              >
                <EditOutlinedIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={REPLACE_PROMPT} placement='top'>
              <IconButton
                size='small'
                onClick={() => onReplace(form)}
                aria-label={`Replace the questions for ${form.display_name}`}
              >
                <FileUploadOutlinedIcon style={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          <IntakeFormActionsMenu
            formName={form.display_name}
            isFormEnabled={form.enabled}
            isActionPending={isActionPending}
            isActionDisabled={isActionDisabled}
            onSelectAction={() => onSetEnabled(!form.enabled)}
          />
        </Box>
      </Box>
    </Paper>
  )
}
